import React from 'react';

interface Props {
  children: React.ReactNode;
  reportId?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ReportErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ReportErrorBoundary] Rendering crash caught:', {
      reportId: this.props.reportId,
      error: error.message,
      stack: info.componentStack,
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.reportId !== this.props.reportId && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center px-6">
          <p className="text-lg font-semibold text-red-400 mb-2">
            This report could not be displayed
          </p>
          <p className="text-sm text-gray-400 max-w-md">
            An error occurred while rendering the report content. Try selecting a
            different report or refreshing the page.
          </p>
          <button
            type="button"
            className="mt-4 px-4 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ReportErrorBoundary;
